import React from 'react';
import { useAuth } from 'oidc-react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useUser } from '@/hooks/useUser';
import { useAgronodKonto } from '@/contexts/AgronodKontoContext';
import { UserRole } from '@/types';
import { ROUTES } from '@/constants';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

/**
 * Roles allowed to reach the main app. New roles must be added here explicitly —
 * this is a runtime check, not a TypeScript-exhaustive one, so adding a role to
 * the enum without updating this set will silently redirect those users to
 * /unauthorized (this is how AGROKOLL_B2B was silently blocked for months).
 */
export const APP_ACCESS_ROLES: ReadonlySet<UserRole> = new Set([
  UserRole.AGROKOLL_ADMIN,
  UserRole.AGROKOLL_OWNER,
  UserRole.AGROKOLL_RADGIVARE,
  UserRole.AGROKOLL_B2B,
  UserRole.AGROKOLL_SYSTEM_ADMIN,
]);

/**
 * Protected route wrapper for OIDC authentication and role-based authorization.
 * With OIDC, users are automatically redirected to Keycloak login if not authenticated.
 * The RealmGuard component handles cross-realm access control.
 *
 * Role check: Users must have AGROKOLL_ADMIN, AGROKOLL_OWNER, AGROKOLL_RADGIVARE, AGROKOLL_B2B,
 * or AGROKOLL_SYSTEM_ADMIN role. Users without proper roles are redirected to /unauthorized.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoading: oidcLoading } = useAuth();
  const { user, isLoading: userLoading } = useUser();
  const { isReady: kontoReady } = useAgronodKonto();

  // Show loading spinner while checking authentication, fetching user data, or waiting for konto to load
  if (oidcLoading || userLoading || !kontoReady) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          gap: 2
        }}>
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{
          color: "text.secondary"
        }}>
          Laddar...
        </Typography>
      </Box>
    );
  }

  const hasRequiredRole = user?.roles?.some((role) => APP_ACCESS_ROLES.has(role));

  if (!hasRequiredRole) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // With OIDC autoSignIn, users are automatically redirected to login
  // RealmGuard handles realm-based redirects
  return <>{children}</>;
};

export default ProtectedRoute;
